// Page "Règles de relance" (Lot 4C admin) — liste des règles J+N + modale de
// création/édition + toggle inline pour activer/désactiver.
//
// Pattern : ClientsPage (liste + Modal) pour la cohérence d'UX. Spécificités :
// - toggle inline `enabled` → PATCH { enabled } sans passer par la modale ;
// - dropdown `template_key` nourri par /api/reminder-templates pour aiguiller
//   vers des clés réellement définies (évite les typos silencieuses) — avec
//   fallback libre si l'admin veut pointer vers une clé custom pas encore créée.

import { FormEvent, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client";
import { reminderRulesApi } from "../api/reminderRules";
import { reminderTemplatesApi } from "../api/reminderTemplates";
import type {
  ReminderRuleCreate,
  ReminderRulePublic,
  ReminderTemplatePublic,
} from "../api/types";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";

// Libellés humains pour les 3 clés seedées ; fallback au raw key sinon.
const TEMPLATE_LABEL: Record<string, string> = {
  reminder_friendly: "Amicale",
  reminder_firm: "Ferme",
  reminder_precontentieux: "Pré-contentieux",
};

function templateLabel(key: string): string {
  return TEMPLATE_LABEL[key] ?? key;
}

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; rule: ReminderRulePublic };

export function ReminderRulesPage() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<ReminderRulePublic[] | null>(null);
  const [templates, setTemplates] = useState<ReminderTemplatePublic[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [toggleError, setToggleError] = useState<string | null>(null);

  async function reload() {
    setListError(null);
    try {
      const data = await reminderRulesApi.list();
      setRules(data);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur de chargement");
      setRules([]);
    }
  }

  async function loadTemplates() {
    try {
      const data = await reminderTemplatesApi.list();
      setTemplates(data);
    } catch {
      // Pas bloquant : la modale retombera sur un input texte libre.
      setTemplates([]);
    }
  }

  useEffect(() => {
    void reload();
    void loadTemplates();
  }, []);

  async function onToggle(rule: ReminderRulePublic) {
    setToggleError(null);
    // Optimiste : on flippe localement, on rollback en cas d'erreur.
    const prev = rules;
    setRules(
      (prev ?? []).map((r) =>
        r.id === rule.id ? { ...r, enabled: !r.enabled } : r,
      ),
    );
    try {
      await reminderRulesApi.update(rule.id, { enabled: !rule.enabled });
    } catch (err) {
      setRules(prev);
      setToggleError(
        err instanceof ApiError ? err.detail : "Impossible de basculer l'état",
      );
    }
  }

  async function onDelete(rule: ReminderRulePublic) {
    if (
      !window.confirm(
        `Supprimer la règle "${rule.name}" (J+${rule.offset_days}) ?\n\n` +
          "Les relances déjà envoyées seront conservées dans l'historique, " +
          "sans référence à cette règle.",
      )
    ) {
      return;
    }
    try {
      await reminderRulesApi.delete(rule.id);
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.detail : "Suppression impossible";
      window.alert(msg);
    }
  }

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {t("reminderRulesPage.title")}
          </h1>
          <p className="text-sm text-slate-500">
            {t("reminderRulesPage.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setDialog({ mode: "create" })}
        >
          + {t("reminderRulesPage.newRule")}
        </button>
      </header>

      {toggleError && (
        <p role="alert" className="mb-4 text-sm text-red-600">
          {toggleError}
        </p>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {listError && (
          <p role="alert" className="p-4 text-sm text-red-600">
            {listError}
          </p>
        )}
        {rules === null && (
          <p className="p-6 text-sm text-slate-500">{t("common.loading")}</p>
        )}
        {rules !== null && rules.length === 0 && !listError && (
          <p className="p-6 text-sm text-slate-500">
            {t("reminderRulesPage.noRules")}
          </p>
        )}
        {rules && rules.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium w-28">{t("reminderRulesPage.colTrigger")}</th>
                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                <th className="px-4 py-2 font-medium">{t("reminderRulesPage.colTemplate")}</th>
                <th className="px-4 py-2 font-medium w-24">{t("reminderRulesPage.colOrder")}</th>
                <th className="px-4 py-2 font-medium w-32 text-center">
                  {t("reminderRulesPage.colActive")}
                </th>
                <th className="px-4 py-2 font-medium w-32 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-900">
                    J+{r.offset_days}
                  </td>
                  <td className="px-4 py-2 text-slate-800">{r.name}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {templateLabel(r.template_key)}
                    <span className="text-xs text-slate-400 ml-2">
                      <code>{r.template_key}</code>
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.sort_order}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={r.enabled}
                      aria-label={`Activer la règle ${r.name}`}
                      onClick={() => onToggle(r)}
                      className={[
                        "relative inline-flex h-6 w-11 items-center rounded-full transition",
                        r.enabled ? "bg-brand-500" : "bg-slate-300",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "inline-block h-4 w-4 transform rounded-full bg-white transition",
                          r.enabled ? "translate-x-6" : "translate-x-1",
                        ].join(" ")}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right space-x-3">
                    <button
                      type="button"
                      className="text-brand-600 hover:underline"
                      onClick={() => setDialog({ mode: "edit", rule: r })}
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      type="button"
                      className="text-red-600 hover:underline"
                      onClick={() => onDelete(r)}
                    >
                      {t("common.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RuleDialog
        state={dialog}
        templates={templates}
        onClose={() => setDialog({ mode: "closed" })}
        onSaved={async () => {
          setDialog({ mode: "closed" });
          await reload();
        }}
      />
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Modale création / édition
// ---------------------------------------------------------------------------

function RuleDialog({
  state,
  templates,
  onClose,
  onSaved,
}: {
  state: DialogState;
  templates: ReminderTemplatePublic[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isOpen = state.mode !== "closed";

  const [values, setValues] = useState<ReminderRuleCreate>(emptyCreate());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset à chaque ouverture / changement de cible.
  useEffect(() => {
    if (state.mode === "create") setValues(emptyCreate());
    else if (state.mode === "edit")
      setValues({
        name: state.rule.name,
        offset_days: state.rule.offset_days,
        template_key: state.rule.template_key,
        enabled: state.rule.enabled,
        sort_order: state.rule.sort_order,
      });
    setError(null);
  }, [state]);

  // Suggestions template : on fusionne les clés renvoyées par l'API avec les
  // 3 clés seedées pour garantir qu'elles apparaissent même si l'API templates
  // a échoué (cf. fallback dans le parent).
  const templateKeys = useMemo(() => {
    const seeded = [
      "reminder_friendly",
      "reminder_firm",
      "reminder_precontentieux",
    ];
    const fromApi = templates.map((t) => t.template_key);
    return Array.from(new Set([...seeded, ...fromApi]));
  }, [templates]);

  if (!isOpen) return null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const payload = {
        name: values.name.trim(),
        offset_days: values.offset_days,
        template_key: values.template_key.trim(),
        enabled: values.enabled,
        sort_order: values.sort_order,
      };
      if (state.mode === "edit") {
        await reminderRulesApi.update(state.rule.id, payload);
      } else {
        await reminderRulesApi.create(payload);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible d'enregistrer");
    } finally {
      setPending(false);
    }
  }

  const title =
    state.mode === "edit" ? t("reminderRulesPage.editRule") : t("reminderRulesPage.newRule");

  return (
    <Modal open={isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={onSubmit} className="space-y-4" aria-label="rule-form">
        <FormField label={t("common.name")} htmlFor="name" required hint="Ex : Relance amicale (J+7)">
          <input
            id="name"
            required
            maxLength={80}
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            className="input"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Délai (jours après échéance)"
            htmlFor="offset_days"
            required
            hint="0 = le jour même ; max 365."
          >
            <input
              id="offset_days"
              type="number"
              required
              min={0}
              max={365}
              value={values.offset_days}
              onChange={(e) =>
                setValues({
                  ...values,
                  offset_days: Number.parseInt(e.target.value, 10) || 0,
                })
              }
              className="input"
            />
          </FormField>

          <FormField
            label="Ordre d'affichage"
            htmlFor="sort_order"
            hint="Tri secondaire dans la liste."
          >
            <input
              id="sort_order"
              type="number"
              min={0}
              max={999}
              value={values.sort_order ?? 0}
              onChange={(e) =>
                setValues({
                  ...values,
                  sort_order: Number.parseInt(e.target.value, 10) || 0,
                })
              }
              className="input"
            />
          </FormField>
        </div>

        <FormField
          label="Template d'email"
          htmlFor="template_key"
          required
          hint="Clé du template qui sera rendu à l'envoi (éditable dans « Templates relances »)."
        >
          <input
            id="template_key"
            required
            list="template-keys"
            maxLength={80}
            value={values.template_key}
            onChange={(e) =>
              setValues({ ...values, template_key: e.target.value })
            }
            className="input"
            placeholder="reminder_friendly"
          />
          <datalist id="template-keys">
            {templateKeys.map((k) => (
              <option key={k} value={k}>
                {templateLabel(k)}
              </option>
            ))}
          </datalist>
        </FormField>

        <FormField label={t("reminderRulesPage.colActive")} htmlFor="enabled">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              id="enabled"
              type="checkbox"
              checked={values.enabled ?? true}
              onChange={(e) =>
                setValues({ ...values, enabled: e.target.checked })
              }
            />
            <span>
              {values.enabled ?? true
                ? "La règle déclenchera des envois automatiques"
                : "La règle est désactivée (pas d'envoi)"}
            </span>
          </label>
        </FormField>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="text-slate-500 hover:text-slate-800 text-sm"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={pending} className="btn-primary">
            {pending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyCreate(): ReminderRuleCreate {
  return {
    name: "",
    offset_days: 7,
    template_key: "reminder_friendly",
    enabled: true,
    sort_order: 0,
  };
}
